import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { trackCtaClick } from '@/lib/tracking';
import { setNis2EntrySource } from '@/lib/nis2/entrySource';

interface HomeHeroSectionProps {
  /** Optional search panel rendered beside the hero copy on desktop, below it on mobile. */
  searchSlot?: ReactNode;
}

export function HomeHeroSection({ searchSlot }: HomeHeroSectionProps) {
  return (
    <section className="relative isolate border-b border-primary-dark bg-primary-dark">
      <div className="container mx-auto px-4 py-10 sm:py-12 lg:py-16">
        <div className="mx-auto max-w-2xl text-center">
          <span className="mb-4 inline-flex items-center rounded-sm border border-primary-foreground/60 bg-primary-foreground/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-primary-foreground sm:text-sm">
            <Sparkles className="h-3 w-3 mr-1.5" />
            Onafhankelijk vergelijkingsplatform
          </span>

          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground mb-4 leading-tight">
            Vind de juiste IT-dienstverlener voor jouw MKB
          </h1>

          <p className="text-base sm:text-lg text-primary-foreground/80 mb-6 max-w-2xl mx-auto">
            Vergelijk Managed Service Providers (MSP's) op kwaliteit, cybersecurity en service — en ontdek welke IT-partner past bij jouw organisatie.
          </p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
            <Button
              asChild
              size="lg"
              variant="hero"
              className="w-full sm:w-auto text-sm sm:text-base"
              onClick={() => trackCtaClick('hero_wizard', 'hero', '/wizard')}
            >
              <Link to="/wizard">
                Vind mijn IT-partner
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </Link>
            </Button>
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
              <Link
                to="/vergelijken"
                onClick={() => trackCtaClick('hero_compare', 'hero', '/vergelijken')}
                className="inline-flex items-center gap-1 text-xs sm:text-sm text-primary-foreground/70 hover:text-primary-foreground underline-offset-2 hover:underline transition-colors"
              >
                Of bekijk alle MSP's
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/nis2-zelfscan"
                onClick={() => {
                  setNis2EntrySource('home_hero');
                  trackCtaClick('hero_nis2_scan', 'hero', '/nis2-zelfscan');
                }}
                className="inline-flex items-center gap-1 text-xs sm:text-sm text-primary-foreground/70 hover:text-primary-foreground underline-offset-2 hover:underline transition-colors"
              >
                Doe de gratis NIS2 Zelfscan
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <p className="mt-4 text-xs sm:text-sm text-primary-foreground/70">
            Vrijblijvend • maximaal 3 matches • commerciële zichtbaarheid duidelijk gemarkeerd • geen spam
          </p>

          {searchSlot ? (
            <div className="mt-8 sm:mt-10 mx-auto max-w-[680px]">
              {searchSlot}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
